import React, { useEffect, useRef, useState } from 'react';
import { PopulationData } from '../services/censusApi';

interface PopulationHeatMapProps {
  map: google.maps.Map | null;
  populationData: PopulationData | null;
  isVisible: boolean;
  opacity: number;
}

const PopulationHeatMap: React.FC<PopulationHeatMapProps> = ({
  map,
  populationData,
  isVisible,
  opacity
}) => {
  const heatmapRef = useRef<google.maps.visualization.HeatmapLayer | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(10);
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null);

  // Listen for map zoom and bounds changes
  useEffect(() => {
    if (!map) return;

    const handleZoomChanged = () => {
      const zoom = (map as any).getZoom?.() || 10;
      setCurrentZoom(zoom);
    };

    const handleBoundsChanged = () => {
      const bounds = (map as any).getBounds?.() || null;
      setMapBounds(bounds);
    };

    // Add event listeners
    (map as any).addListener?.('zoom_changed', handleZoomChanged);
    (map as any).addListener?.('bounds_changed', handleBoundsChanged);

    // Set initial values
    handleZoomChanged();
    handleBoundsChanged();

    // Cleanup
    return () => {
      if ((google.maps as any).event?.clearListeners) {
        (google.maps as any).event.clearListeners(map, 'zoom_changed');
        (google.maps as any).event.clearListeners(map, 'bounds_changed');
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !populationData || !isVisible) {
      // Remove heatmap if it exists
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
        heatmapRef.current = null;
      }
      return;
    }

    // Calculate density ranges using percentiles for better distribution
    const densities = populationData.tracts.map(t => t.density).filter(d => d > 0).sort((a, b) => a - b);
    
    // Use percentiles to create better color distribution
    const p20 = densities[Math.floor(densities.length * 0.2)];
    const p40 = densities[Math.floor(densities.length * 0.4)];
    const p60 = densities[Math.floor(densities.length * 0.6)];
    const p80 = densities[Math.floor(densities.length * 0.8)];
    const minDensity = densities[0];
    const maxDensity = densities[densities.length - 1];
    
    console.log('Density percentiles:', { 
      minDensity, p20, p40, p60, p80, maxDensity,
      totalTracts: densities.length 
    });
    
    // Create heatmap data points with weighted intensity
    const heatmapData: Array<google.maps.LatLng | google.maps.visualization.WeightedLocation> = [];
    
    // Calculate zoom-based adjustments
    const zoomFactor = Math.max(1, currentZoom - 8); // More detail at higher zoom levels
    const pointMultiplier = Math.min(5, zoomFactor); // Cap at 5x points for performance
    
    populationData.tracts.forEach(tract => {
      if (tract.density <= 0) return;
      
      // Check if tract is within current map bounds (if available)
      if (mapBounds) {
        const tractCenter = new google.maps.LatLng(
          (tract.bounds.north + tract.bounds.south) / 2,
          (tract.bounds.east + tract.bounds.west) / 2
        );
        if (!(mapBounds as any).contains?.(tractCenter)) {
          return; // Skip tracts outside current view
        }
      }
      
      // Calculate density level based on percentiles - normalize to similar visibility levels
      let densityLevel = 0;
      if (tract.density <= p20) densityLevel = 0.8; // Very low - but still visible
      else if (tract.density <= p40) densityLevel = 0.85; // Low
      else if (tract.density <= p60) densityLevel = 0.9; // Medium
      else if (tract.density <= p80) densityLevel = 0.95; // High
      else densityLevel = 1.0; // Very high
      
      // Create multiple points per tract based on population and zoom level
      const basePoints = Math.max(1, Math.floor(tract.population / (300 / pointMultiplier)));
      
      for (let i = 0; i < basePoints; i++) {
        // Generate points with more concentration in the center of tracts
        const centerLat = (tract.bounds.north + tract.bounds.south) / 2;
        const centerLng = (tract.bounds.east + tract.bounds.west) / 2;
        
        // Adjust radius based on zoom level - smaller radius at higher zoom for more detail
        const baseRadius = Math.sqrt(tract.bounds.north - tract.bounds.south) * 0.3;
        const radius = baseRadius / Math.max(1, zoomFactor * 0.5);
        
        const angle = Math.random() * 2 * Math.PI;
        const distance = Math.random() * radius;
        
        const lat = centerLat + distance * Math.cos(angle);
        const lng = centerLng + distance * Math.sin(angle);
        
        // Ensure points stay within tract bounds
        const clampedLat = Math.max(tract.bounds.south, Math.min(tract.bounds.north, lat));
        const clampedLng = Math.max(tract.bounds.west, Math.min(tract.bounds.east, lng));
        
        // Create weighted location based on density level
        const location = new google.maps.LatLng(clampedLat, clampedLng);
        
        heatmapData.push({
          location,
          weight: densityLevel
        });
      }
    });
    
    console.log('Heat map data points:', heatmapData.length);
    console.log('Current zoom level:', currentZoom);
    console.log('Map bounds:', mapBounds);

    // Create heatmap layer
    if (window.google && window.google.maps && window.google.maps.visualization) {
      // Adjust radius based on zoom level
      const baseRadius = 50;
      const zoomRadius = Math.max(20, baseRadius / Math.max(1, currentZoom - 8));
      
      heatmapRef.current = new window.google.maps.visualization.HeatmapLayer({
        data: heatmapData,
        map: map,
        radius: zoomRadius,
        opacity: opacity,
        gradient: [
          'rgba(0, 128, 0, 0.1)',      // Green (very low density) - visible
          'rgba(0, 128, 0, 0.3)',      // Green (very low density)
          'rgba(255, 255, 0, 0.5)',    // Yellow (low density)
          'rgba(255, 192, 203, 0.7)',  // Pink (medium density)
          'rgba(255, 0, 0, 0.8)',      // Red (high density)
          'rgba(128, 0, 128, 0.9)',    // Purple (very high density)
          'rgba(128, 0, 128, 0.95)',   // Purple (very high density)
          'rgba(128, 0, 128, 1)',      // Purple (very high density)
          'rgba(128, 0, 128, 1)',      // Purple (very high density)
          'rgba(128, 0, 128, 1)'       // Purple (maximum density)
        ]
      });
    } else {
      console.warn('Google Maps Visualization library not loaded');
    }

    // Cleanup function
    return () => {
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
        heatmapRef.current = null;
      }
    };
  }, [map, populationData, isVisible, opacity, currentZoom, mapBounds]);

  // Update opacity when it changes
  useEffect(() => {
    if (heatmapRef.current) {
      heatmapRef.current.setOptions({ opacity });
    }
  }, [opacity]);

  // Update visibility when it changes
  useEffect(() => {
    if (heatmapRef.current) {
      heatmapRef.current.setMap(isVisible ? map : null);
    }
  }, [isVisible, map]);

  return null; // This component doesn't render any DOM elements
};

export default PopulationHeatMap; 